import { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { startOfWeek, endOfWeek, subWeeks } from 'date-fns';
import moment from 'moment';

import { FProgressBar } from '../../components/base';
import {
  claimsActions,
  clientActions,
  clientSupervisionActions,
  parentSupervisionActions,
  serviceCodeActions,
  sessionActions,
  clientAssessmentActions,
  groupParentSupervisionActions,
  facilityActions,
  cohortActions,
  providerActions,
} from '../../store';
import ClaimBuilder from './ClaimBuilder';
import ClaimDetails from '../../components/layout/billings/claims/ClaimDetails';
import { useClaimsSelectors } from './hooks/useClaimsSelectors';
import ClaimReviewDialog from '../../components/layout/billings/claims/ClaimReviewDialog';
import Claim1500FormInfo from '../../components/layout/billings/claim-info/Claim1500FormInfo';
import ClaimServiceLineDetails from '../../components/layout/billings/claim-info/ClaimServiceLineDetails';
import { FORTA_BLUE } from '../../utils/colors';
import { getUtcOffset } from '../../utils/common';
import { history } from '../../helpers';
import PageContainer from '../../components/layout/PageContainer';
import { asDefinedOr } from '../../components/layout/billings/claims/utils';
import {
  filterSessionsByDateRangeAndBay,
  getClaimedSessions,
  getNotReadySessions,
  getProviderIdsForLicensingAndPayorCredentials,
  getUnclaimedSessions,
  isRelaxClaimValidationsActive,
} from './utils/general';
import {
  selectedDateForBillingWeekly,
  billingLagInWeeks,
  SessionClaimStatus,
  ServiceCodes,
  AllowedServiceCodesToBuildClaim,
} from '../../components/layout/billings/claims/constants';
import { CT_TIMEZONE_AREA, Roles } from '../../constants';
import { TabPanel, a11yProps } from './common';
import { ClinicalStatus } from '../../admin/constants/general';
import ExclamationIcon from '../../components/svgs/ExclamationIcon';
import { ClaimSourceEnum, PROCEDURE_DURATION_TO_UNIT_FACTOR } from '../constants';

import type {
  ISessionWithClaimStatus,
  IServiceCode,
  IOption,
  IClientPolicyMap,
} from '../../utils/types';

const fieldContainerStyle = {
  mb: 3,
};

const fieldLabelStyle = {
  fontWeight: 700,
  textAlign: 'left',
};

const fieldContentStyle = {
  textAlign: 'left',
  width: 'fit-content',
};

const bay2_6BillingWeekLag = 2;

const disableDifferentAuthorsWithDifferentClinicalRel = (
  session: ISessionWithClaimStatus,
  firstSelectedSession: ISessionWithClaimStatus,
  selectedProcedureCode: IServiceCode | null,
) => {
  return (
    selectedProcedureCode?.code === ServiceCodes.SessionProcedureCode &&
    session.author_id !== firstSelectedSession.author_id &&
    session.clinical_relationship !== firstSelectedSession.clinical_relationship
  );
};

function Claims() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const {
    user,
    providerLicensesMapping,
    providerPayorCredentialsMapping,
    providerLicensesLoading,
    providerPayorCredentialsLoading,
    providerLicensesErrors,
    providerPayorCredentialsErrors,
    clients,
    clientsLoading,
    serviceCodes,
    serviceCodesLoading,
    sessionsWithClaimStatus,
    sessionsLoading,
    clientSNWithClaimStatus,
    clientSNLoading,
    parentSNWithClaimStatus,
    parentSNLoading,
    gpsnWithClaimStatus,
    assessmentsWithClaimStatus,
    assessmentsLoading,
    assistingAssessmentsWithClaimStatus,
    assistingAssessmentsLoading,
    claimsLoading,
    claimPreview,
    selectedClientPlans,
  } = useClaimsSelectors();
  const [claimSource, setClaimSource] = useState<ClaimSourceEnum | null>(null);

  const [procedureCodes, setProcedureCodes] = useState<IServiceCode[]>([]);
  const [selectedProcedureCode, setSelectedProcedureCode] = useState<IServiceCode | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<IOption | null>(null);
  const [selectedClient, setSelectedClient] = useState<IOption | null>(null);
  const [calendarDate, setCalendarDate] = useState<{ startDate: Date; endDate: Date }>(() => {
    const laggedDate = subWeeks(
      selectedDateForBillingWeekly,
      Math.min(bay2_6BillingWeekLag, billingLagInWeeks),
    );
    return {
      startDate: startOfWeek(laggedDate),
      endDate: endOfWeek(laggedDate),
    };
  });
  const [weekPickerDate, setWeekPickerDate] = useState<Date>(() => {
    return subWeeks(
      selectedDateForBillingWeekly,
      Math.min(bay2_6BillingWeekLag, billingLagInWeeks),
    );
  });
  const [hideClaimed, setHideClaimed] = useState<boolean>(true);
  const [isSelectedAll, setIsSelectedAll] = useState<boolean>(false);
  const [selectedTableKey, setSelectedTableKey] = useState<string>('');
  const [showClaimReviewDialog, setShowClaimReviewDialog] = useState<boolean>(false);
  const [isExperimentalClaim, setIsExperimentalClaim] = useState<boolean>(false);
  const [useDefaultBillingFacility, setUseDefaultBillingFacility] = useState<boolean>(false);
  const [selectedBillingFacilityId, setSelectedBillingFacilityId] = useState<number | null>(null);
  const [relaxClaimValidationsChecked, setRelaxClaimValidationsChecked] = useState(false);
  const [relaxClaimValidationsPassword, setRelaxClaimValidationsPassword] = useState('');
  const [sessions, setSessions] = useState<ISessionWithClaimStatus[]>([]);
  const [claimedSessions, setClaimedSessions] = useState<ISessionWithClaimStatus[]>([]);
  const [filteredSessions, setFilteredSessions] = useState<ISessionWithClaimStatus[]>([]);
  const [noOfSessions, setNoOfSessions] = useState<number>(0);
  const [selectedProcedureIds, setSelectedProcedureIds] = useState<(number | string)[]>([]);
  const [durationOfSessionsSelected, setDurationOfSessionsSelected] = useState<number>(0);
  const [numberOfUnitsSelected, setNumberOfUnitsSelected] = useState<number>(0);
  const [providerName, setProviderName] = useState<string>('');
  const [tab, setTab] = useState<number>(0);
  const [searchParams] = useSearchParams();
  const isInitialLoad = useRef(true);

  const relaxValidations = useMemo(
    () =>
      isRelaxClaimValidationsActive(relaxClaimValidationsChecked, relaxClaimValidationsPassword),
    [relaxClaimValidationsChecked, relaxClaimValidationsPassword],
  );

  useEffect(() => {
    dispatch(claimsActions.updateState({ key: 'claimPreview', data: null }));
  }, [isExperimentalClaim, selectedBillingFacilityId, useDefaultBillingFacility, relaxValidations]);

  const selectedPolicyDetails = useMemo(() => {
    if (!selectedPolicy) return undefined;
    return selectedClientPlans.find((policy: IClientPolicyMap) => policy.id === selectedPolicy?.id);
  }, [selectedClientPlans, selectedPolicy]);

  useEffect(() => {
    dispatch(
      clientActions.getList({
        getAccessTokenSilently,
        clinical_status: [
          ClinicalStatus.CLINICAL,
          ClinicalStatus.OFFBOARDED,
          ClinicalStatus.ON_HOLD,
        ],
      }),
    );
    dispatch(
      serviceCodeActions.getList({
        getAccessTokenSilently,
        params: { is_timesheet_usable: false },
      }),
    );
    dispatch(facilityActions.getList({ getAccessTokenSilently }));
    return () => {
      dispatch(clientActions.clearSelectedClient());
      dispatch(claimsActions.updateState({ key: 'claimPreview', data: null }));
      dispatch(providerActions.clearProviderLicensingAndCredentialDetails());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!claimPreview) return;
    dispatch(claimsActions.updateState({ key: 'claimPreview', data: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noOfSessions, selectedProcedureIds, selectedClient]);

  useEffect(() => {
    if (!selectedPolicy || !selectedProcedureCode) return;
    dispatch(
      clientActions.getPolicyPriorAuthorizationDetails({
        policyId: selectedPolicy?.id,
        serviceCode: selectedProcedureCode.id,
        getAccessTokenSilently,
      }),
    );
  }, [selectedPolicy, selectedProcedureCode]);

  useEffect(() => {
    if (!serviceCodes) {
      setProcedureCodes([]);
      return;
    }
    const allowedServiceCodes = Object.values(AllowedServiceCodesToBuildClaim);
    const procedureCodes = serviceCodes
      .filter((serviceCode: IServiceCode) =>
        allowedServiceCodes.some((allowedCode) => allowedCode === serviceCode.code),
      )
      .map((serviceCode: IServiceCode) => ({
        ...serviceCode,
        label: `${serviceCode.label} (${serviceCode.code})`,
      }));

    setProcedureCodes(procedureCodes);
    setSelectedProcedureCode(
      procedureCodes.find(
        (serviceCode) => serviceCode.code === ServiceCodes.SessionProcedureCode,
      ) ?? null,
    );
  }, [serviceCodes]);

  useEffect(() => {
    if (!selectedClient || !selectedProcedureCode) return;
    dispatch(
      clientActions.getClientSpecificFacilityMapping({
        getAccessTokenSilently,
        clientId: selectedClient?.id,
      }),
    );
    dispatch(
      clientActions.getClientSpecificPayorsMapping({
        getAccessTokenSilently,
        clientId: selectedClient?.id,
      }),
    );
    dispatch(
      cohortActions.getClientCohortAssignments({
        params: { client_id: selectedClient?.id },
        getAccessTokenSilently,
      }),
    );
  }, [selectedClient, selectedProcedureCode]);

  useEffect(() => {
    if (filteredSessions.length === 0 || !selectedPolicy || selectedClientPlans?.length === 0)
      return;

    const selectedBillingPayor = selectedClientPlans.find(
      (policy) => policy.id === selectedPolicy?.id,
    )?.billing_payor;

    if (!selectedBillingPayor) return;

    const { providerIdsForLicensing, providerIdsForPayorCredentials } =
      getProviderIdsForLicensingAndPayorCredentials(
        providerLicensesMapping,
        providerPayorCredentialsMapping,
        filteredSessions,
        selectedBillingPayor,
      );

    if (providerIdsForLicensing.length > 0) {
      providerIdsForLicensing.forEach((key) => {
        const error = providerLicensesErrors[key];
        const [providerId, _] = key.split('-');
        if (error) return;
        dispatch(
          providerActions.getProviderLicensingDetails({
            providerId: parseInt(providerId),
            getAccessTokenSilently,
            state: selectedBillingPayor.state,
          }),
        );
      });
    }
    if (providerIdsForPayorCredentials.length > 0) {
      providerIdsForPayorCredentials.forEach((key) => {
        const [providerId, payorId] = key.split('-');
        const error = providerPayorCredentialsErrors[key];
        if (error) return;
        dispatch(
          providerActions.getProviderPayorCredentials({
            providerId: parseInt(providerId),
            payorId: parseInt(payorId),
            getAccessTokenSilently,
          }),
        );
      });
    }
  }, [filteredSessions, selectedPolicy, selectedClientPlans]);

  useEffect(() => {
    if (!selectedClient || !selectedProcedureCode || !calendarDate) return;

    switch (selectedProcedureCode.code) {
      case ServiceCodes.SessionProcedureCode:
        dispatch(
          sessionActions.getSessionsWithClaimStatus({
            params: {
              startDate: moment(calendarDate.startDate).subtract(1, 'day'),
              endDate: moment(calendarDate.endDate).add(1, 'day'),
              client_id: selectedClient?.id,
              include_qa_stats: true,
              expand: Roles.Bcba,
            },
            getAccessTokenSilently,
          }),
        );
        break;
      case ServiceCodes.ClientSNProcedureCode:
        dispatch(
          clientSupervisionActions.getSupervisionsWithClaimStatus({
            params: {
              startDate: moment(calendarDate.startDate).subtract(1, 'day'),
              endDate: moment(calendarDate.endDate).add(1, 'day'),
              client_id: selectedClient?.id,
            },
            getAccessTokenSilently,
          }),
        );
        break;
      case ServiceCodes.ParentSNProcedureCode:
        dispatch(
          parentSupervisionActions.getSupervisionsWithClaimStatus({
            params: {
              startDate: moment(calendarDate.startDate).subtract(1, 'day'),
              endDate: moment(calendarDate.endDate).add(1, 'day'),
              client_id: selectedClient?.id,
            },
            getAccessTokenSilently,
          }),
        );
        break;
      case ServiceCodes.AssessmentCode:
        dispatch(
          clientAssessmentActions.getAssessmentsWithClaimStatus({
            params: {
              startDate: moment(calendarDate.startDate).subtract(1, 'day'),
              endDate: moment(calendarDate.endDate).add(1, 'day'),
              client_id: selectedClient?.id,
            },
            getAccessTokenSilently,
          }),
        );
        break;
      case ServiceCodes.AssistingAssessmentCode:
        dispatch(
          clientAssessmentActions.getAssistingAssessmentsWithClaimStatus({
            params: {
              startDate: moment(calendarDate.startDate).subtract(1, 'day'),
              endDate: moment(calendarDate.endDate).add(1, 'day'),
              client_id: selectedClient?.id,
            },
            getAccessTokenSilently,
          }),
        );
        break;
      case ServiceCodes.GroupSNProcedureCode:
        dispatch(
          groupParentSupervisionActions.getGPSNWithClaimStatus({
            params: {
              startDate: moment(calendarDate.startDate).subtract(1, 'day'),
              endDate: moment(calendarDate.endDate).add(1, 'day'),
              client_id: selectedClient?.id,
            },
            getAccessTokenSilently,
          }),
        );
        break;
      default:
        break;
    }

    return () => {
      dispatch(sessionActions.updateState({ key: 'sessionsWithClaimStatus', data: [] }));
      dispatch(
        clientSupervisionActions.updateState({ key: 'supervisionsWithClaimStatus', data: [] }),
      );
      dispatch(
        parentSupervisionActions.updateState({ key: 'supervisionsWithClaimStatus', data: [] }),
      );
      dispatch(
        groupParentSupervisionActions.updateState({ key: 'supervisionsWithClaimStatus', data: [] }),
      );
      dispatch(clientAssessmentActions.updateState({ key: 'assementsWithClaimStatus', data: [] }));
      dispatch(
        clientAssessmentActions.updateState({
          key: 'assistingAssessmentsWithClaimStatus',
          data: [],
        }),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarDate, selectedClient, selectedProcedureCode]);

  useEffect(() => {
    let sessions: ISessionWithClaimStatus[] = [];
    if (
      !selectedProcedureCode ||
      providerLicensesLoading ||
      providerPayorCredentialsLoading ||
      !selectedPolicy ||
      !selectedClientPlans
    ) {
      return;
    }

    switch (selectedProcedureCode.code) {
      case ServiceCodes.SessionProcedureCode:
        sessions = sessionsWithClaimStatus;
        break;
      case ServiceCodes.ClientSNProcedureCode:
        sessions = clientSNWithClaimStatus;
        break;
      case ServiceCodes.ParentSNProcedureCode:
        sessions = parentSNWithClaimStatus;
        break;
      case ServiceCodes.AssessmentCode:
        sessions = assessmentsWithClaimStatus;
        break;
      case ServiceCodes.AssistingAssessmentCode:
        sessions = assistingAssessmentsWithClaimStatus;
        break;
      case ServiceCodes.GroupSNProcedureCode:
        sessions = gpsnWithClaimStatus;
        break;
      default:
        break;
    }

    setSessions(sessions);
  }, [
    selectedPolicy?.id,
    selectedClientPlans,
    selectedProcedureCode,
    sessionsWithClaimStatus,
    clientSNWithClaimStatus,
    parentSNWithClaimStatus,
    assessmentsWithClaimStatus,
    gpsnWithClaimStatus,
    assistingAssessmentsWithClaimStatus,
    providerLicensesMapping,
    providerPayorCredentialsMapping,
    providerLicensesLoading,
    providerPayorCredentialsLoading,
  ]);

  useEffect(() => {
    const filteredWeeklySessions = filterSessionsByDateRangeAndBay(sessions, calendarDate);

    const claimedSessions = getClaimedSessions(filteredWeeklySessions);
    const unClaimedSessions = getUnclaimedSessions(filteredWeeklySessions);
    const notReadySessions = getNotReadySessions(filteredWeeklySessions);

    setClaimedSessions(claimedSessions);
    const records = (
      hideClaimed
        ? [...unClaimedSessions, ...notReadySessions]
        : [...unClaimedSessions, ...notReadySessions, ...claimedSessions]
    ).sort(
      (sessionA: ISessionWithClaimStatus, sessionB: ISessionWithClaimStatus) =>
        moment(sessionB.date).unix() - moment(sessionA.date).unix(),
    );
    setFilteredSessions(records);
    setProviderName('');
    setIsSelectedAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions, hideClaimed]);

  useEffect(() => {
    const selectedSessions = filteredSessions.filter(
      (billingSession: ISessionWithClaimStatus) => billingSession.selected,
    );
    setSelectedProcedureIds(selectedSessions.map((session) => session.id));
    setNoOfSessions(selectedSessions.length);
    setProviderName(
      noOfSessions
        ? (filteredSessions.find((session) => session.selected)?.provider_name ?? '')
        : '',
    );
    const selectedSessionsDuration = filteredSessions
      .filter((seesion) => seesion.selected)
      .reduce(
        (totalDuration: number, session: ISessionWithClaimStatus) =>
          totalDuration + asDefinedOr(session.duration, 0),
        0,
      );
    setDurationOfSessionsSelected(selectedSessionsDuration);
    setNumberOfUnitsSelected(selectedSessionsDuration / PROCEDURE_DURATION_TO_UNIT_FACTOR);
  }, [noOfSessions, filteredSessions]);

  useEffect(() => {
    if (!claimPreview) return;
    if (tab === 0) {
      setTab(1);
    }
  }, [claimPreview]);

  useEffect(() => {
    const handleUrlParams = async () => {
      const clientId = searchParams.get('client_id');
      const serviceCode = searchParams.get('service_code');
      const procedureIds = searchParams.get('procedure_ids')?.split(',');
      const dateParam = searchParams.get('date');

      if (!clientId) return;

      const client = clients.find((c) => c.id === parseInt(clientId));

      if (client) {
        // Store procedure IDs in state for later use
        if (procedureIds?.length) {
          const numericProcedureIds = procedureIds.map((id) => parseInt(id));
          setSelectedProcedureIds(numericProcedureIds);
          setNoOfSessions(numericProcedureIds.length);
        }

        // Set client
        setSelectedClient({
          id: client.id,
          label: `${client.first_name} ${client.last_name}`,
          description:
            client.clinical_status === ClinicalStatus.OFFBOARDED ||
            client.clinical_status === ClinicalStatus.ON_HOLD
              ? client.clinical_status
              : '',
        });

        // Set bay and calculate appropriate lag
        let weekLagValue = billingLagInWeeks; // Default lag
        let procedure = null;
        // Set procedure code
        if (serviceCode) {
          procedure = procedureCodes.find((p) => p.code === serviceCode);
          if (procedure) {
            setSelectedProcedureCode(procedure);
            if (procedure.code === ServiceCodes.SessionProcedureCode) {
              weekLagValue = Math.min(bay2_6BillingWeekLag, billingLagInWeeks);
            }
          }
        }

        // Set date with lag consideration
        if (dateParam) {
          const date = moment(dateParam).add(1, 'day');

          // Check if the date respects the lag period
          const minAllowedDate = subWeeks(selectedDateForBillingWeekly, weekLagValue);

          // Check if date is BEFORE or EQUAL to the minimum allowed date
          if (moment(date).isSameOrBefore(minAllowedDate)) {
            setWeekPickerDate(date.toDate());
            setCalendarDate({
              startDate: startOfWeek(date.toDate()),
              endDate: endOfWeek(date.toDate()),
            });
          } else {
            toast.error(
              procedure?.code === ServiceCodes.SessionProcedureCode
                ? `Claims for sessions within the past ${Math.min(bay2_6BillingWeekLag, billingLagInWeeks)} weeks are not allowed`
                : `Claims for sessions within the past ${billingLagInWeeks} weeks are not allowed`,
            );
            setWeekPickerDate(minAllowedDate);
            setCalendarDate({
              startDate: startOfWeek(minAllowedDate),
              endDate: endOfWeek(minAllowedDate),
            });
            return;
          }
        }
      }
    };

    if (!clientsLoading && !serviceCodesLoading && clients.length && procedureCodes.length) {
      handleUrlParams();
    }
  }, [searchParams, clients, procedureCodes, clientsLoading, serviceCodesLoading]);

  useEffect(() => {
    const handleSessionSelection = async () => {
      const procedureIds = searchParams.get('procedure_ids')?.split(',');
      const serviceCode = searchParams.get('service_code');
      const clientId = searchParams.get('client_id');
      const dateParam = searchParams.get('date');

      // Return early if date violates billing lag
      if (dateParam) {
        const date = new Date(dateParam);
        const weekLagValue =
          selectedProcedureCode?.code === ServiceCodes.SessionProcedureCode
            ? Math.min(bay2_6BillingWeekLag, billingLagInWeeks)
            : billingLagInWeeks;
        const minAllowedDate = subWeeks(selectedDateForBillingWeekly, weekLagValue);
        if (!moment(date).isSameOrBefore(minAllowedDate)) {
          return;
        }
      }
      if (procedureIds?.length && filteredSessions.length > 0) {
        const numericProcedureIds = procedureIds.map((id) => parseInt(id));

        const updatedSessions = filteredSessions.map((session) => ({
          ...session,
          selected: numericProcedureIds.includes(Number(session.id)),
        }));

        setFilteredSessions(updatedSessions);

        if (serviceCode && clientId && procedureCodes.length > 0 && isInitialLoad.current) {
          const procedure = procedureCodes.find((p) => p.code === serviceCode);
          if (procedure) {
            const response = await dispatch(
              claimsActions.createClaimPreview({
                data: {
                  client_id: parseInt(clientId),
                  service_code_id: procedure.id,
                  procedure_ids: numericProcedureIds,
                  skip_prior_authorization: false,
                },
                getAccessTokenSilently,
              }),
            );
            setClaimSource(ClaimSourceEnum.RETOOL_LINK);
            isInitialLoad.current = false;
            if (!response || response.error) return;
            setTab(1);
          }
        }
      }
    };

    handleSessionSelection();
  }, [filteredSessions.length, searchParams, procedureCodes]);

  const handleProcedureChange = (selectedProcedure: IServiceCode) => {
    const weekLagValue =
      selectedProcedure.code === ServiceCodes.SessionProcedureCode
        ? Math.min(bay2_6BillingWeekLag, billingLagInWeeks)
        : billingLagInWeeks;
    setSelectedProcedureCode(selectedProcedure);
    if (
      moment(weekPickerDate).isSameOrAfter(subWeeks(selectedDateForBillingWeekly, weekLagValue))
    ) {
      setCalendarDate({
        startDate: startOfWeek(subWeeks(selectedDateForBillingWeekly, weekLagValue)),
        endDate: endOfWeek(subWeeks(selectedDateForBillingWeekly, weekLagValue)),
      });
      setWeekPickerDate(subWeeks(selectedDateForBillingWeekly, weekLagValue));
    }
  };

  const handleSelectRow = (
    selected: boolean,
    session: ISessionWithClaimStatus,
    selectedTableKey: string,
    bay: string | null,
    clinicalRelationship: string | null,
    year: string | null,
    month: string | null,
    providerId: number | null | undefined,
  ) => {
    setSelectedTableKey(selectedTableKey);
    //update select all checkbox
    if (!selected) setIsSelectedAll(false);

    const filteredBillingSessions = filteredSessions.map(
      (billingSession: ISessionWithClaimStatus) => {
        if (session.id === billingSession.id) {
          return {
            ...billingSession,
            selected,
          };
        }
        const isYearSame = moment(billingSession.date).tz(CT_TIMEZONE_AREA).format('YYYY') === year;
        const isMonthSame = moment(billingSession.date).tz(CT_TIMEZONE_AREA).format('MM') === month;
        const isProviderSame = billingSession.provider_id === providerId;
        const isClinicalRelationshipSame =
          !billingSession.clinical_relationship ||
          billingSession.clinical_relationship === clinicalRelationship;
        const isBaySame = !billingSession.bay || billingSession.bay === bay;
        if (
          billingSession.selected &&
          (!isYearSame ||
            !isMonthSame ||
            !isProviderSame ||
            !isClinicalRelationshipSame ||
            !isBaySame)
        ) {
          return {
            ...billingSession,
            selected: false,
          };
        }
        return billingSession;
      },
    );

    // update select all checkbox
    if (selected) {
      setIsSelectedAll(false);
    }

    setFilteredSessions(filteredBillingSessions);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handleSelectAll = (
    selected: boolean,
    selectedTableKey: string,
    bay: string | null,
    clinicalRelationship: string | null,
    year: string | null,
    month: string | null,
    providerId: number | null | undefined,
  ) => {
    setSelectedTableKey(selectedTableKey);
    setIsSelectedAll(selected);
    if (filteredSessions.length === 0) return;

    const firstBillingSession = filteredSessions.find(
      (session: ISessionWithClaimStatus) =>
        session.status === SessionClaimStatus.Unclaimed &&
        (!session.bay || session.bay === bay) &&
        (!session.clinical_relationship ||
          session.clinical_relationship === clinicalRelationship) &&
        moment(session.date).tz(CT_TIMEZONE_AREA).format('YYYY') === year &&
        moment(session.date).tz(CT_TIMEZONE_AREA).format('MM') === month &&
        (providerId == null || session.provider_id === providerId),
    );

    if (!firstBillingSession) return;

    const records = filteredSessions.map((billingSession: ISessionWithClaimStatus) => {
      if (billingSession.status !== SessionClaimStatus.Unclaimed) return billingSession;

      const isYearSame = moment(billingSession.date).tz(CT_TIMEZONE_AREA).format('YYYY') === year;
      const isMonthSame = moment(billingSession.date).tz(CT_TIMEZONE_AREA).format('MM') === month;
      const isProviderSame = providerId == null || billingSession.provider_id === providerId;
      const isBaySame = !billingSession.bay || billingSession.bay === bay;

      if (
        selected &&
        (!isBaySame ||
          !isProviderSame ||
          billingSession.readonly ||
          firstBillingSession?.provider_id !== billingSession.provider_id ||
          !isYearSame ||
          !isMonthSame ||
          (firstBillingSession &&
            disableDifferentAuthorsWithDifferentClinicalRel(
              billingSession,
              firstBillingSession,
              selectedProcedureCode,
            )))
      )
        return {
          ...billingSession,
          selected: false,
        };

      return {
        ...billingSession,
        selected,
      };
    });

    setFilteredSessions(records);
  };

  const handleHideClaimed = (selected: boolean) => {
    setHideClaimed(selected);
    setFilteredSessions(
      (selected
        ? filteredSessions.filter(
            (billingSession: ISessionWithClaimStatus) =>
              billingSession.status === SessionClaimStatus.Unclaimed,
          )
        : [...filteredSessions, ...claimedSessions]
      ).sort(
        (sessionA: ISessionWithClaimStatus, sessionB: ISessionWithClaimStatus) =>
          moment(sessionB.date).unix() - moment(sessionA.date).unix(),
      ),
    );
  };

  const handleGenerateClaim = async () => {
    if (
      !selectedDateForBillingWeekly ||
      !selectedClient ||
      !selectedProcedureCode ||
      !filteredSessions
    )
      return;

    setShowClaimReviewDialog(false);

    const response = await dispatch(
      claimsActions.createClaim({
        data: {
          date_generated: selectedDateForBillingWeekly,
          utc_offset_date_generated: getUtcOffset(moment(selectedDateForBillingWeekly)),
          client_id: selectedClient?.id,
          service_code_id: selectedProcedureCode?.id,
          skip_prior_authorization: isExperimentalClaim,
          billing_facility_id: selectedBillingFacilityId,
          use_custom_billing_facility: useDefaultBillingFacility,
          relax_validations: relaxValidations,
          procedure_ids: filteredSessions
            .filter((session) => session.selected)
            .map((session) => session.id),
          source: claimSource,
        },
        getAccessTokenSilently,
      }),
    );

    if (!response || response.error) return;

    history.navigate(`/billings/claims/${response.payload.id}`);
  };
  const previewTabsDisabled = !(
    selectedProcedureCode?.code &&
    selectedClient &&
    noOfSessions > 0 &&
    claimPreview
  );

  return (
    <PageContainer
      sx={{
        pl: 4.5,
        pt: 3,
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
      dataTestId={'billingsPageContainer'}
    >
      {(clientsLoading ||
        sessionsLoading ||
        clientSNLoading ||
        parentSNLoading ||
        assessmentsLoading ||
        assistingAssessmentsLoading ||
        serviceCodesLoading ||
        claimsLoading) && <FProgressBar />}
      <Box display={'flex'} flexDirection={'column'}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{
            minHeight: 32,
            '& .MuiTab-root': {
              padding: '0 0.5rem',
              textTransform: 'none',
              fontWeight: 400,
              minHeight: 32,
              color: FORTA_BLUE.primary,
            },
          }}
        >
          <Tab label="Build claim" {...a11yProps(0)} data-testid="buildClaimTab" />
          <Tab
            label="1500 form preview"
            {...a11yProps(1)}
            data-testid="claim1500PreviewTab"
            disabled={previewTabsDisabled}
          />
          <Tab
            label="Claim details & service line details preview"
            {...a11yProps(2)}
            data-testid="claimDetailsAndServicelineDetailsPreviewTab"
            disabled={previewTabsDisabled}
          />
        </Tabs>
        <TabPanel value={tab} index={0}>
          <ClaimBuilder
            noOfSessions={noOfSessions}
            numberOfUnitsSelected={numberOfUnitsSelected}
            clients={clients}
            selectedPolicy={selectedPolicy}
            calendarDate={calendarDate}
            isSelectedAll={isSelectedAll}
            filteredSessions={filteredSessions}
            bay2_6BillingWeekLag={bay2_6BillingWeekLag}
            billingLagInWeeks={billingLagInWeeks}
            weekPickerDate={weekPickerDate}
            sessionsLoading={sessionsLoading}
            hideClaimed={hideClaimed}
            selectedTableKey={selectedTableKey}
            procedureCodes={procedureCodes}
            selectedClient={selectedClient}
            selectedProcedureCode={selectedProcedureCode}
            selectedPolicyDetails={selectedPolicyDetails}
            setSelectedPolicy={setSelectedPolicy}
            setCalendarDate={setCalendarDate}
            handleHideClaimed={handleHideClaimed}
            setWeekPickerDate={setWeekPickerDate}
            setSelectedClient={setSelectedClient}
            handleSelectProcedure={handleProcedureChange}
            handleSelectAll={handleSelectAll}
            handleSelectRow={handleSelectRow}
          />
          <Box sx={{ mt: 3 }}>
            <ClaimDetails
              generatePreview={tab === 0}
              numberOfUnitsSelected={numberOfUnitsSelected}
              isExperimentalClaim={isExperimentalClaim}
              selectedBillingFacilityId={selectedBillingFacilityId}
              useDefaultBillingFacility={useDefaultBillingFacility}
              authorizer={user?.given_name}
              bcbaName={providerName}
              totalNoOfSessions={noOfSessions}
              selectedProcedureIds={selectedProcedureIds}
              totalDurationOfSessions={durationOfSessionsSelected}
              selectedClient={selectedClient}
              setTab={setTab}
              setUseDefaultBillingFacility={setUseDefaultBillingFacility}
              setSelectedBillingFacility={setSelectedBillingFacilityId}
              setIsExperimentalClaim={setIsExperimentalClaim}
              onGenerate={() => setShowClaimReviewDialog(true)}
              setClaimSourceLink={(source: ClaimSourceEnum) => setClaimSource(source)}
              selectedProcedure={selectedProcedureCode ?? undefined}
              relaxClaimValidationsChecked={relaxClaimValidationsChecked}
              relaxClaimValidationsPassword={relaxClaimValidationsPassword}
              relaxValidations={relaxValidations}
              setRelaxClaimValidationsChecked={setRelaxClaimValidationsChecked}
              setRelaxClaimValidationsPassword={setRelaxClaimValidationsPassword}
            />
          </Box>
        </TabPanel>
        <TabPanel value={tab} index={1}>
          {claimPreview ? (
            <>
              <Claim1500FormInfo
                showPreview
                fieldContainerStyle={fieldContainerStyle}
                fieldLabelStyle={fieldLabelStyle}
                fieldContentStyle={{ ...fieldContentStyle, ml: '34px' }}
              />
              <Box sx={{ mt: 3 }}>
                <ClaimDetails
                  generatePreview={tab === 0}
                  numberOfUnitsSelected={numberOfUnitsSelected}
                  isExperimentalClaim={isExperimentalClaim}
                  selectedBillingFacilityId={selectedBillingFacilityId}
                  useDefaultBillingFacility={useDefaultBillingFacility}
                  authorizer={user?.given_name}
                  bcbaName={providerName}
                  totalNoOfSessions={noOfSessions}
                  selectedProcedureIds={selectedProcedureIds}
                  totalDurationOfSessions={durationOfSessionsSelected}
                  selectedClient={selectedClient}
                  setTab={setTab}
                  setUseDefaultBillingFacility={setUseDefaultBillingFacility}
                  setSelectedBillingFacility={setSelectedBillingFacilityId}
                  setIsExperimentalClaim={setIsExperimentalClaim}
                  onGenerate={() => setShowClaimReviewDialog(true)}
                  setClaimSourceLink={(source: ClaimSourceEnum) => setClaimSource(source)}
                  selectedProcedure={selectedProcedureCode ?? undefined}
                  relaxClaimValidationsChecked={relaxClaimValidationsChecked}
                  relaxClaimValidationsPassword={relaxClaimValidationsPassword}
                  relaxValidations={relaxValidations}
                  setRelaxClaimValidationsChecked={setRelaxClaimValidationsChecked}
                  setRelaxClaimValidationsPassword={setRelaxClaimValidationsPassword}
                />
              </Box>
            </>
          ) : previewTabsDisabled ? null : (
            <Box
              sx={{
                pt: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
              }}
            >
              <ExclamationIcon />
              <Typography
                variant="body1"
                sx={{ ml: 2, fontWeight: '700', color: FORTA_BLUE.light1 }}
              >
                Click the &quot;Create preview&quot; button to view a claim preview before
                generating. Once a claim is generated it cannot be deleted.
              </Typography>
            </Box>
          )}
        </TabPanel>
        <TabPanel value={tab} index={2}>
          {claimPreview ? (
            <ClaimServiceLineDetails
              showPreview
              fieldContainerStyle={fieldContainerStyle}
              fieldContentStyle={fieldContentStyle}
              fieldLabelStyle={fieldLabelStyle}
            />
          ) : previewTabsDisabled ? null : (
            <Box
              sx={{
                pt: 3,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
              }}
            >
              <ExclamationIcon />
              <Typography
                variant="body1"
                sx={{ ml: 2, fontWeight: '700', color: FORTA_BLUE.light1 }}
              >
                Click the &quot;Create preview&quot; button to view a claim preview before
                generating. Once a claim is generated it cannot be deleted.
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Box>
      <ClaimReviewDialog
        show={showClaimReviewDialog}
        title="Review Claim"
        selectedPolicyDetails={selectedPolicyDetails}
        sessions={filteredSessions}
        authorizer={user?.given_name}
        providerName={providerName}
        totalNoOfSessions={noOfSessions}
        totalDurationOfSessions={durationOfSessionsSelected ?? 0}
        displayClient={selectedClient?.label ?? ''}
        selectedProcedureCode={selectedProcedureCode?.code || ''}
        onClose={() => setShowClaimReviewDialog(false)}
        onSubmit={() => handleGenerateClaim()}
      />
    </PageContainer>
  );
}

export default Claims;
